import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { User, Mail, Lock, Eye, EyeOff, Save, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const Settings = () => {
  const { t } = useLanguage();
  const { user, updateProfile } = useAuth();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords if changing
    if (formData.new_password) {
      if (formData.new_password !== formData.confirm_password) {
        setError('Yeni parollar uyğun gəlmir');
        return;
      }
      if (!formData.current_password) {
        setError('Cari parol tələb olunur');
        return;
      }
    }

    setLoading(true);
    try {
      const updateData = {};
      if (formData.name !== user?.name) updateData.name = formData.name;
      if (formData.email !== user?.email) updateData.email = formData.email;
      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      if (Object.keys(updateData).length > 0) {
        await updateProfile(updateData);
        setSuccess('Parametrlər uğurla yeniləndi');
        setFormData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Xəta baş verdi');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">
          {t('admin_settings')}
        </h1>
        <p className="text-[#4A5D43] text-sm mt-1">Profil parametrlərini idarə edin</p>
      </div>

      <div className="max-w-xl">
        <div className="bg-white border border-[#E5E3DB] rounded-lg p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2">
              <Check className="w-5 h-5" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Section */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-[#1A2415] flex items-center gap-2">
                <User className="w-5 h-5 text-[#2D5016]" />
                Profil Məlumatları
              </h2>

              <div>
                <Label className="mb-2 block">Ad</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="settings-name-input"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="settings-email-input"
                />
              </div>
            </div>

            {/* Password Section */}
            <div className="space-y-4 pt-6 border-t border-[#E5E3DB]">
              <h2 className="text-lg font-medium text-[#1A2415] flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#2D5016]" />
                Parol Dəyişikliyi
              </h2>

              <div>
                <Label className="mb-2 block">Cari Parol</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={formData.current_password}
                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    className="pr-10"
                    data-testid="settings-current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5D43]"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Yeni Parol</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    className="pr-10"
                    data-testid="settings-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5D43]"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Yeni Parolu Təsdiqlə</Label>
                <Input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  data-testid="settings-confirm-password-input"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#2D5016] hover:bg-[#1A2415] w-full sm:w-auto"
                data-testid="save-settings-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('save')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
